import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SFToggleButton from "../../../toggleButton";
import { SFMoreVerticalIcon } from "../../../icons";
import IconTypeTooltip from "../../components/iconTypeTooltip";
import IconMenu from "../../components/iconMenu";
import getStyled from "../../../../hooks/getStyled";

const { styled } = getStyled();

const ToggleButtonContentWrapper = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: "4px",
  justifyContent: "center",
}));

const MoreAction = forwardRef(
  ({ overflowDefs = [], onToggle = () => {}, disableTooltip = false }, ref) => {
    // Create a state to track if the toggle button is selected
    const [selected, setSelected] = useState(false);

    // Create a ref to the toggle button for the icon menu to anchor to
    const toggleButtonRef = useRef();

    // Expose the deselect method to the parent component using the ref
    useImperativeHandle(ref, () => ({
      deselect: () => {
        setSelected(false);
      },
    }));

    const { t } = useTranslation();

    const handleClick = () => {
      // Toggle the selected state
      setSelected((prev) => !prev);
      // Call the parent onToggle function with the new selected state
      onToggle(!selected);
    };

    return (
      <>
        <SFToggleButton
          centerRipple
          onClick={handleClick}
          ref={toggleButtonRef}
          selected={selected}
          value="more"
        >
          <IconTypeTooltip tooltip={t("components.actionBar.tooltips.more")} disableTooltip={disableTooltip}>
            {/* We need to wrap the icon div or the tooltip will not work */}
            <ToggleButtonContentWrapper>
              <SFMoreVerticalIcon />
            </ToggleButtonContentWrapper>
          </IconTypeTooltip>
        </SFToggleButton>
        <IconMenu
          actionDefs={overflowDefs}
          anchorEl={toggleButtonRef.current}
          handleClose={() => {
            setSelected(false);
            onToggle(false);
          }}
          open={selected}
          variant="overflow"
          selectedItems={overflowDefs
            .filter((def) => def.children?.length > 0 && def.selectedChild)
            .map((def) => def.selectedChild)}
        />
      </>
    );
  }
);

export default MoreAction;

MoreAction.propTypes = {
  // The overflow definitions for the more action
  overflowDefs: PropTypes.arrayOf(
    PropTypes.shape({
      // The name of the action
      name: PropTypes.string.isRequired,
      // The function to call when the action is clicked
      onClick: PropTypes.func.isRequired,
      // Props specific to the icon type action (the only type supported for overflow actions)
      iconProps: PropTypes.shape({
        // The icon to display
        icon: PropTypes.element,
        // The label of the icon when it is in the overflow menu
        overflowLabel: PropTypes.string,
      }),
      // The children of the action
      children: PropTypes.arrayOf(
        PropTypes.shape({
          // The name of the child action
          name: PropTypes.string.isRequired,
          // The label of the child
          label: PropTypes.string.isRequired,
          // The function to call when the child is clicked
          onClick: PropTypes.func.isRequired,
        })
      ),
    })
  ),
  // The function to call when the more action is toggled
  onToggle: PropTypes.func,
};
